/**
 * @description
 * Normalize Lynx API error payloads into user-facing message strings for UI banners.
 *
 * @dependencies
 * - None (pure utility)
 *
 * @notes
 * - Recurring plan routes return `error` (summary) plus `errors` (string[]).
 * - Some endpoints return structured `{ field, message, code }` objects — both
 *   shapes are handled here.
 */

/**
 * True when axios got no HTTP body (CORS-hidden 5xx, timeout, offline).
 *
 * @param {unknown} err
 * @returns {boolean}
 */
function isUnreachableClientError(err) {
  if (err?.response) {
    return false;
  }
  const code = err?.code;
  if (code === 'ERR_NETWORK' || code === 'ECONNABORTED' || code === 'ERR_CANCELED') {
    return true;
  }
  const message = typeof err?.message === 'string' ? err.message : '';
  return /network error/i.test(message) || /timeout of \d+ms exceeded/i.test(message);
}

/**
 * @param {unknown} err - Axios-style error or generic Error
 * @param {string} [fallback='Something went wrong']
 * @returns {string[]}
 */
export function getApiErrorMessages(err, fallback = 'Something went wrong') {
  const data = err?.response?.data;

  if (!data) {
    if (isUnreachableClientError(err)) {
      return [fallback];
    }
    const message = err?.message;
    return [typeof message === 'string' && message ? message : fallback];
  }

  // Schedule hard-block: prefer explicit API copy, with a stable fallback.
  if (data.code === 'PTO_SCHEDULE_CONFLICT') {
    if (typeof data.error === 'string' && data.error) return [data.error];
    return ['Cannot book this shift — the employee has approved time off covering this time.'];
  }

  if (Array.isArray(data.errors) && data.errors.length > 0) {
    const messages = data.errors
      .map((entry) => {
        if (typeof entry === 'string') return entry;
        if (entry && typeof entry.message === 'string') return entry.message;
        return null;
      })
      .filter(Boolean);
    if (messages.length > 0) return messages;
  }

  if (typeof data.error === 'string' && data.error) {
    return [data.error];
  }

  if (data.error && typeof data.error === 'object' && typeof data.error.message === 'string' && data.error.message) {
    return [data.error.message];
  }

  return [fallback];
}

/**
 * @param {unknown} err
 * @param {string} [fallback]
 * @returns {string}
 */
export function getApiErrorMessage(err, fallback) {
  const messages = getApiErrorMessages(err, fallback);
  if (messages.length === 1) return messages[0];
  return messages.join(' ');
}
